import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
    Activity, Server, Cpu, AlertTriangle, Clock, Zap, Target, BarChart2, Brain,
    DollarSign, Gauge, Thermometer, Shield, TrendingUp, TrendingDown, Network, CloudLightning, Wind, Settings
} from 'lucide-react';

// Helper for unique IDs
const uid = () => Math.random().toString(36).substring(2, 10);

// --- Configuration ---
const TICK_INTERVAL_MS = 1000;
const SCALING_CHECK_INTERVAL_S = 2;
const INSTANCE_STARTUP_S = 15;
const MAX_INSTANCES = 12;
const LOG_HISTORY_COUNT = 12;
const PREDICTION_HORIZON_S = 10;

// *** TRAFFIC SPEED ADJUSTMENTS (MUCH SLOWER) ***
const MAX_RPS_CHANGE_PER_SEC = 1;  // Max NORMAL change per second (Drastically reduced)
const BASE_RPS = 15;               // Lower starting point
const PRIMARY_WAVE_PERIOD_S = 120; // Very slow primary wave (was 90)
const SECONDARY_WAVE_PERIOD_S = 70;  // Very slow secondary wave (was 45)
const PRIMARY_WAVE_AMPLITUDE = 35; // Lower amplitude (was 40)
const SECONDARY_WAVE_AMPLITUDE = 10; // Lower amplitude (was 15)
const NOISE_AMPLITUDE = 10;        // Lower noise target range (was 15)
const ANOMALY_CHANCE_MAJOR = 0.002; // VERY rare major anomaly (was 0.005)
const ANOMALY_CHANCE_MEDIUM = 0.01; // Rare medium anomaly (was 0.02)
const ANOMALY_MAGNITUDE_MAJOR = [150, 250]; // Reduced major spike (was [300, 500])
const ANOMALY_MAGNITUDE_MEDIUM = [30, 80];   // Reduced medium spike (was [50, 150])
const MIN_TIME_MAJOR_ANOMALY = 60;  // Don't allow major spikes before T=60s
const MIN_TIME_MEDIUM_ANOMALY = 30; // Don't allow medium spikes before T=30s


// --- Default Instance Type Definitions ---
const defaultInstanceTypes = [
    { name: 't4g.nano', capacity: 20, costPerHour: 0.02, carbonIntensity: 0.5 },
    { name: 't4g.2xlarge', capacity: 80, costPerHour: 0.15, carbonIntensity: 0.8 },
    { name: 'p3.2xlarge', capacity: 150, costPerHour: 0.40, carbonIntensity: 1.2 },
    { name: 'g4dn.8xlarge', capacity: 250, costPerHour: 0.90, carbonIntensity: 1.8 },
    { name: 'g5.12xlarge', capacity: 400, costPerHour: 1.60, carbonIntensity: 2.5 },
    { name: 'p4d.24xlarge', capacity: 600, costPerHour: 3.50, carbonIntensity: 3.5 }
];

// --- Main Component ---
export const Autoscale = ({ overridePricing = {} }) => {

    // --- Merge Default Prices with Overrides ---
    const instanceTypes = useMemo(() => {
        return defaultInstanceTypes.map(instType => ({
            ...instType,
            costPerHour: overridePricing[instType.name] !== undefined ? overridePricing[instType.name] : instType.costPerHour,
            startup: INSTANCE_STARTUP_S
        })).sort((a, b) => a.capacity - b.capacity);
    }, [overridePricing]);


    // --- State ---
    const [instances, setInstances] = useState([]);
    const [requestsPerSecond, setRequestsPerSecond] = useState(BASE_RPS);
    const [requestTypeDist, setRequestTypeDist] = useState({ api: 0.6, web: 0.3, background: 0.1 });
    const [predictedLoadTrend, setPredictedLoadTrend] = useState(Array(PREDICTION_HORIZON_S).fill(BASE_RPS));
    const [logs, setLogs] = useState([]);
    const [time, setTime] = useState(0);
    const [systemStatus, setSystemStatus] = useState('Initializing');
    const [aiStrategy, setAiStrategy] = useState('Balanced');
    const [aiConfidence, setAiConfidence] = useState(0.75);
    const [avgLatency, setAvgLatency] = useState(30);
    const [costEfficiencyScore, setCostEfficiencyScore] = useState(0.8);
    const [threatLevel, setThreatLevel] = useState(0);
    const [networkIo, setNetworkIo] = useState({ in: 0, out: 0 });
    const [totalCarbonEstimate, setTotalCarbonEstimate] = useState(0);

    const prevRpsRef = useRef(requestsPerSecond);
    const logsEndRef = useRef(null);

    // --- Logging Utility ---
    const addLog = useCallback((message, type = 'info', icon = null) => {
        setLogs(prev => {
            const timestamp = new Date().toLocaleTimeString('en-US', { hour12: false });
            const newLog = { id: uid(), message, timestamp, type, icon };
            return [...prev, newLog].slice(-LOG_HISTORY_COUNT);
        });
    }, []);

    // Scroll logs into view
    useEffect(() => { logsEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [logs]);

    // --- System Initialization & Timer ---
    useEffect(() => {
        addLog('Foundry Autoscale System v5.2 Initializing...', 'system', <Brain size={12} className="text-cyan-400" />);
        addLog('Predictive Scaling Engine Engaged.', 'system', <TrendingUp size={12} />);
        addLog('Dynamic Fleet Management Protocols Active.', 'system', <Settings size={12} />);
        setSystemStatus('Calibrating');

        const timer = setInterval(() => setTime(t => t + 1), TICK_INTERVAL_MS);
        return () => clearInterval(timer);
    }, [addLog]);

    // --- Core Simulation Loop (Traffic, AI Predictions, Metrics) ---
    useEffect(() => {
        const updateSimulation = () => {
            // --- 1. Calculate Target Traffic (Very Slow) ---
            const baseTarget = BASE_RPS
                + Math.sin((time / PRIMARY_WAVE_PERIOD_S) * 2 * Math.PI) * PRIMARY_WAVE_AMPLITUDE
                + Math.sin((time / SECONDARY_WAVE_PERIOD_S) * 2 * Math.PI) * SECONDARY_WAVE_AMPLITUDE;
            const noiseTarget = (Math.random() - 0.48) * NOISE_AMPLITUDE;
            let targetRps = Math.max(5, baseTarget + noiseTarget);
            let isMajorAnomaly = false;

            // --- Heavily Reduced Anomaly Logic ---
            const threatRoll = Math.random();
            if (threatRoll < ANOMALY_CHANCE_MAJOR && time > MIN_TIME_MAJOR_ANOMALY) {
                const spike = ANOMALY_MAGNITUDE_MAJOR[0] + Math.random() * (ANOMALY_MAGNITUDE_MAJOR[1] - ANOMALY_MAGNITUDE_MAJOR[0]);
                targetRps += spike;
                setThreatLevel(3);
                addLog(`CRITICAL ANOMALY! Surge +${spike.toFixed(0)}. Threat Level HIGH!`, 'error', <CloudLightning size={12} className="animate-ping" />);
                isMajorAnomaly = true;
            } else if (threatRoll < ANOMALY_CHANCE_MEDIUM && time > MIN_TIME_MEDIUM_ANOMALY) {
                const spike = ANOMALY_MAGNITUDE_MEDIUM[0] + Math.random() * (ANOMALY_MAGNITUDE_MEDIUM[1] - ANOMALY_MAGNITUDE_MEDIUM[0]);
                targetRps += spike;
                setThreatLevel(prev => Math.min(3, Math.max(2, prev + 1)));
                addLog(`Elevated Traffic Event +${spike.toFixed(0)}. Threat: Moderate.`, 'warning', <AlertTriangle size={12} />);
            } else if (threatRoll < 0.10) { // Keep minor probe chance low
                setThreatLevel(prev => Math.min(3, Math.max(1, prev)));
            } else {
                // Slow threat decay
                setThreatLevel(prev => Math.max(0, prev - (Math.random() < 0.08 ? 1 : 0)));
            }

            // --- Apply Smoothing (Very Strict Limit) ---
            let nextRps;
            const currentRps = prevRpsRef.current;
            const desiredChange = targetRps - currentRps;

            if (isMajorAnomaly) {
                nextRps = Math.max(5, Math.round(targetRps)); // Allow jump only for verified major anomaly
            } else {
                const maxChange = MAX_RPS_CHANGE_PER_SEC; // Use the very low limit
                const actualChange = Math.max(-maxChange, Math.min(maxChange, desiredChange));
                nextRps = Math.max(5, Math.round(currentRps + actualChange));
            }

            setRequestsPerSecond(nextRps);
            prevRpsRef.current = nextRps;


            // --- Request Type Distribution ---
            setRequestTypeDist(prev => {
                const apiVar = prev.api + (Math.random() - 0.5) * 0.03; // Even less variation
                const webVar = prev.web + (Math.random() - 0.5) * 0.02;
                const normApi = Math.max(0.1, Math.min(0.8, apiVar));
                const normWeb = Math.max(0.1, Math.min(0.8, webVar));
                const normBg = Math.max(0, 1.0 - normApi - normWeb);
                const sum = normApi + normWeb + normBg;
                return { api: normApi / sum, web: normWeb / sum, background: normBg / sum };
            });

            // --- 2. AI Prediction (Based on Slow Waves) ---
            const futurePoints = Array(PREDICTION_HORIZON_S).fill(0).map((_, i) => {
                const futureTime = time + i + 1;
                let predicted = BASE_RPS
                    + Math.sin((futureTime / PRIMARY_WAVE_PERIOD_S) * 2 * Math.PI) * PRIMARY_WAVE_AMPLITUDE
                    + Math.sin((futureTime / SECONDARY_WAVE_PERIOD_S) * 2 * Math.PI) * SECONDARY_WAVE_AMPLITUDE;
                predicted += (Math.random() - 0.45) * NOISE_AMPLITUDE; // Reduced prediction noise
                if (isMajorAnomaly && i < 5) {
                    const spikeMagnitude = Math.max(0, targetRps - (baseTarget + noiseTarget));
                    predicted += spikeMagnitude * Math.pow(0.4, i + 1); // Faster predicted decay for the smaller spikes
                }
                return Math.max(5, Math.round(predicted));
            });
            setPredictedLoadTrend(futurePoints);

            // --- 3. Calculate Current Fleet Capacity and Utilization ---
            const currentRunningInstances = instances.filter(inst => inst.state === 'running');
            const activeCapacity = currentRunningInstances.reduce((sum, i) => sum + i.capacity, 0);
            const utilization = activeCapacity > 0 ? Math.min(100, (nextRps / activeCapacity) * 100) : 0;
            const instanceCount = instances.length;

            // --- 4. Update Instance Health & Calculate Instance-Specific Metrics ---
            let totalCost = 0;
            let totalCarbon = 0;
            setInstances(prevInstances => prevInstances.map(inst => {
                let health = inst.health;
                const typeInfo = instanceTypes.find(t => t.name === inst.type);
                if (inst.state === 'running') {
                    const loadFactor = inst.load / (inst.capacity + 1e-6);
                    totalCost += typeInfo?.costPerHour || 0;
                    totalCarbon += typeInfo?.carbonIntensity || 0;

                    let healthChange = -0.004; // Slower base decay
                    if (loadFactor > 0.9) healthChange -= 0.03 * (loadFactor - 0.9); // Slower decay under load
                    if (threatLevel > 1) healthChange -= threatLevel * 0.01; // Even less health impact from threat
                    if (loadFactor < 0.4) healthChange += 0.006; // Slower recovery
                    health = Math.min(1.0, Math.max(0.1, health + healthChange));
                }
                return Math.abs(health - inst.health) > 0.001 ? { ...inst, health: parseFloat(health.toFixed(3)) } : inst;
            }));

            // --- 5. Calculate Derived Global Metrics ---
            let latency = 15 + utilization * 0.5 + (threatLevel * 8) + (Math.random() - 0.5) * 5; // Lowered latency impact
            if (utilization > 95) latency *= 1.4;
            setAvgLatency(Math.max(8, Math.round(latency))); // Lower min latency

            const efficiency = activeCapacity > 0 ? (utilization / 100) / (totalCost / (instanceCount * 0.5 + 0.1)) : 0.3;
            setCostEfficiencyScore(Math.min(1, Math.max(0, 0.5 + efficiency * 0.5 + (Math.random() - 0.5) * 0.1)));
            setTotalCarbonEstimate(totalCarbon);
            const baseNetwork = nextRps * 0.05 + instanceCount * 2; // Lower network sim
            setNetworkIo({ in: baseNetwork * (0.8 + Math.random() * 0.4), out: baseNetwork * (1.0 + Math.random() * 0.6) });

            // --- 6. Update AI Confidence & System Status ---
            const predictionError = Math.abs(predictedLoadTrend[0] - nextRps) / (nextRps + 5);
            let confidenceDelta = -0.005;
            if (predictionError < 0.2) confidenceDelta += 0.03; else confidenceDelta -= predictionError * 0.06; // More tolerant
            if (threatLevel > 1) confidenceDelta -= threatLevel * 0.03;
            if (utilization > 95 || utilization < 10 && activeCapacity > 0) confidenceDelta -= 0.015; // Less penalty
            setAiConfidence(prev => Math.min(0.98, Math.max(0.30, prev + confidenceDelta))); // Higher floor

            const startingInstances = instances.some(i => i.state === 'starting');
            const terminatingInstances = instances.some(i => i.state === 'terminating');
             if (threatLevel >= 3) setSystemStatus('EMERGENCY - ANOMALY');
             else if (utilization > 95 && activeCapacity > 0) setSystemStatus('CRITICAL LOAD');
             else if (threatLevel == 2) setSystemStatus('THREAT LEVEL: HIGH');
             else if (utilization > 85) setSystemStatus('HIGH LOAD');
             else if (threatLevel == 1) setSystemStatus('THREAT LEVEL: LOW');
             else if (startingInstances) setSystemStatus('SCALING UP');
             else if (terminatingInstances) setSystemStatus('OPTIMIZING FLEET');
              // Underutilized check adjusted for very low base load
             else if (utilization < 30 && activeCapacity > (instanceTypes[1]?.capacity || 80)) setSystemStatus('UNDERUTILIZED');
             else setSystemStatus('NOMINAL');
        };
        updateSimulation();
    }, [time, instances, addLog, instanceTypes]);


    // --- Advanced Auto-Scaling Logic ---
    useEffect(() => {
        if (time === 0 || time % SCALING_CHECK_INTERVAL_S !== 0) return;

        const manageInstances = () => {
            const running = instances.filter(i => i.state === 'running');
            const starting = instances.filter(i => i.state === 'starting');

            const currentCapacity = running.reduce((sum, i) => sum + i.capacity, 0);
            const pendingCapacity = starting.reduce((sum, i) => sum + i.capacity, 0);
            const effectiveCapacity = currentCapacity + pendingCapacity;

            const lookaheadSeconds = Math.ceil(INSTANCE_STARTUP_S / SCALING_CHECK_INTERVAL_S) + 2;
            const maxPredictedInHorizon = Math.max(requestsPerSecond, ...predictedLoadTrend.slice(0, lookaheadSeconds));
            // Buffers adjusted for potentially lower confidence with slow changes
            const confidenceBuffer = 1.0 + (1.0 - aiConfidence) * 0.5;
            const threatBuffer = 1.0 + threatLevel * 0.15; // Keep threat buffer slightly higher just in case
            const targetCapacity = maxPredictedInHorizon * confidenceBuffer * threatBuffer;

            // Determine AI Strategy
            let currentStrategy = 'Balanced';
            const currentCost = running.reduce((sum, i) => {
                const typeInfo = instanceTypes.find(t => t.name === i.type);
                return sum + (typeInfo?.costPerHour || 0);
            }, 0);
            const currentUtilization = currentCapacity > 0 ? Math.min(100, (requestsPerSecond / currentCapacity) * 100) : 0;
             // Adjust strategy thresholds for lower baseline load
            if (currentCost > 2.0 || (costEfficiencyScore < 0.6 && currentUtilization < 30)) currentStrategy = 'Cost-Optimized';
            else if (currentUtilization > 70 || avgLatency > 60 || threatLevel > 0) currentStrategy = 'Performance';
            setAiStrategy(currentStrategy);

            // Scaling Thresholds
            const scaleUpThresholdFactor = currentStrategy === 'Performance' ? 0.60 : 0.70; // More aggressive scale up thresholds
            const scaleDownThresholdFactor = currentStrategy === 'Cost-Optimized' ? 0.15 : 0.25; // More aggressive scale down
            const scaleUpThreshold = effectiveCapacity * scaleUpThresholdFactor;
            const scaleDownThreshold = currentCapacity * scaleDownThresholdFactor;

            // ** Scale Up Decision **
            if (targetCapacity > scaleUpThreshold && (running.length + starting.length) < MAX_INSTANCES) {
                const deficit = Math.max(5, targetCapacity - effectiveCapacity); // Ensure minimum deficit to trigger scale up if needed

                // *** CORRECTED Instance Selection Logic ***
                let selectedType = null;
                // Find the smallest instance type that *at least* covers the deficit + a small buffer maybe?
                const requiredCapacity = deficit * 1.1; // Aim to cover deficit + 10% buffer
                for (const type of instanceTypes) {
                    if (type.capacity >= requiredCapacity) {
                        selectedType = type;
                        break; // Found the smallest instance that meets the requirement
                    }
                }
                // If deficit is larger than any single instance can cover, pick the largest
                if (!selectedType && instanceTypes.length > 0) {
                    selectedType = instanceTypes[instanceTypes.length - 1];
                     addLog(`Deficit (${deficit.toFixed(0)}) exceeds largest instance. Selecting max capacity: ${selectedType.name}.`, 'warning');
                }
                 // If NO instance meets even the base deficit (unlikely with t4g.nano but safety check)
                 else if (!selectedType && instanceTypes.length > 0) {
                      // Fallback to find *any* instance larger than current deficit if exact fit failed
                      for (const type of instanceTypes) {
                           if (type.capacity >= deficit) {
                                selectedType = type;
                                break;
                           }
                      }
                      // If still nothing, pick largest (should have been caught above, but extra safe)
                      if (!selectedType) selectedType = instanceTypes[instanceTypes.length - 1];
                      addLog(`No instance perfectly fits deficit (${deficit.toFixed(0)}). Falling back to: ${selectedType.name}.`, 'warning');
                 }


                if (!selectedType) {
                     addLog(`AI Scaling Error: No suitable instance type found.`, 'error');
                     return;
                }
                // *** END INSTANCE SELECTION LOGIC ***

                addLog(`AI [${currentStrategy}]: Target capacity (${targetCapacity.toFixed(0)}) needs +${deficit.toFixed(0)} req/s. Selecting ${selectedType.name}.`, 'system', <Zap size={12} />);

                const newInstance = {
                    id: uid(), type: selectedType.name, capacity: selectedType.capacity,
                    costPerHour: selectedType.costPerHour, carbonIntensity: selectedType.carbonIntensity,
                    startTime: time, readyTime: time + selectedType.startup,
                    state: 'starting', load: 0, health: 1.0,
                };
                setInstances(prev => [...prev, newInstance]);
                addLog(`Provisioning ${selectedType.name} [${newInstance.id.substring(0, 4)}] (+${selectedType.capacity} cap).`, 'success', <Server size={12} />);


            // ** Scale Down Decision **
            } else if (running.length > 1 && requestsPerSecond < scaleDownThreshold && predictedLoadTrend[0] < scaleDownThreshold * 1.1) {
                 addLog(`AI [${currentStrategy}]: Load (${requestsPerSecond}/${predictedLoadTrend[0]} pred) below threshold (${scaleDownThreshold.toFixed(0)}). Evaluating optimization.`, 'system', <DollarSign size={12} />);

                 // Termination selection logic remains the same
                 const sortedRunnable = [...running].sort((a, b) => {
                     if (Math.abs(a.health - b.health) > 0.1) return a.health - b.health;
                     const utilA = a.load / (a.capacity + 1e-6);
                     const utilB = b.load / (b.capacity + 1e-6);
                     if (Math.abs(utilA - utilB) > 0.05) return utilA - utilB;
                     const costA = instanceTypes.find(t => t.name === a.type)?.costPerHour || 99;
                     const costB = instanceTypes.find(t => t.name === b.type)?.costPerHour || 99;
                     if (Math.abs(costA - costB) > 0.01) return costB - costA;
                     return a.capacity - b.capacity;
                 });


                if (sortedRunnable.length > 0) {
                    const toTerminate = sortedRunnable[0];
                    const capacityAfterTermination = currentCapacity - toTerminate.capacity;
                     // Check against predicted load + buffer (using current confidence/threat) - Adjusted buffer slightly maybe?
                     const requiredFutureCapacity = predictedLoadTrend[0] * (1.0 + (1.0 - aiConfidence) * 0.5) * (1.0 + threatLevel * 0.15) * 1.1; // Added 10% safety margin
                    if (capacityAfterTermination >= requiredFutureCapacity) {
                        addLog(`Decommissioning ${toTerminate.type} [${toTerminate.id.substring(0, 4)}] (Health: ${(toTerminate.health * 100).toFixed(0)}%). Reason: Optimization.`, 'info', <Server size={12} className="text-red-400" />);
                        setInstances(prev => prev.map(i => i.id === toTerminate.id ? { ...i, state: 'terminating', terminateTime: time } : i));
                    } else {
                        addLog(`Termination candidate ${toTerminate.type} [${toTerminate.id.substring(0, 4)}] deferred: Required future capacity ${requiredFutureCapacity.toFixed(0)} vs ${capacityAfterTermination.toFixed(0)} post-termination.`, 'info', <AlertTriangle size={12} />);
                    }
                }
            }
        };

        manageInstances();
    }, [time, requestsPerSecond, predictedLoadTrend, instances, aiConfidence, threatLevel, addLog, avgLatency, costEfficiencyScore, instanceTypes]);


    // --- Instance State Progression & Load Distribution ---
    useEffect(() => {
        let needsUpdate = false;
        const nextInstances = instances.map(instance => {
            let updatedInstance = { ...instance };
            if (updatedInstance.state === 'starting' && time >= updatedInstance.readyTime) {
                updatedInstance.state = 'running';
                needsUpdate = true;
                addLog(`Instance ${updatedInstance.type} [${updatedInstance.id.substring(0, 4)}] ACTIVE.`, 'success', <Zap size={12} />);
            } else if (updatedInstance.state === 'terminating' && time >= instance.terminateTime + 4) {
                needsUpdate = true;
                addLog(`Instance ${updatedInstance.type} [${updatedInstance.id.substring(0, 4)}] removed from fleet.`, 'info');
                return null;
            }
            return updatedInstance;
        }).filter(Boolean);

        const currentRunning = nextInstances.filter(i => i.state === 'running');
        const totalRunningCapacity = currentRunning.reduce((sum, i) => sum + i.capacity, 0);
        const finalInstances = nextInstances.map(instance => {
            let newLoad = 0;
            if (instance.state === 'running' && totalRunningCapacity > 0) {
                newLoad = Math.min(instance.capacity, Math.round((instance.capacity / totalRunningCapacity) * requestsPerSecond));
            }
            if (instance.load !== newLoad) {
                needsUpdate = true;
                return { ...instance, load: newLoad };
            }
            return instance;
        });

        if (needsUpdate || finalInstances.length !== instances.length) {
            setInstances(finalInstances);
        }

    }, [time, requestsPerSecond, instances, addLog]);


    // --- UI Helper Functions ---
     const getStatusColor = (instance) => {
         if (instance.state === 'terminating') return 'text-red-600 opacity-50';
         if (instance.state === 'starting') return 'text-yellow-400 animate-pulse';
         if (instance.state === 'running') {
             if (instance.health < 0.4) return 'text-red-500 animate-pulse';
             if (instance.health < 0.7) return 'text-orange-400';
             const loadPerc = instance.capacity > 0 ? instance.load / instance.capacity : 0;
             if (loadPerc > 0.9) return 'text-orange-400';
             if (loadPerc > 0.6) return 'text-yellow-400';
             return 'text-green-400';
         }
         return 'text-gray-500';
     };

     const getStatusIcon = (instance) => {
         const color = getStatusColor(instance);
         if (instance.state === 'starting') return <Clock size={12} className={`mr-1 ${color}`} />;
         if (instance.state === 'running') {
             if (instance.health < 0.4) return <Thermometer size={12} className={`mr-1 ${color}`} />;
             return <div className={`w-1.5 h-1.5 rounded-full mr-1 ${color} shadow-md ${color === 'text-green-400' ? 'shadow-green-500/50 animate-pulse' : ''}`} style={{ backgroundColor: 'currentColor' }}></div>;
         }
         if (instance.state === 'terminating') return <Clock size={12} className={`mr-1 ${color}`} />;
         return <div className="w-1.5 h-1.5 rounded-full mr-1 bg-gray-600"></div>;
     }

     const ThreatLevelIndicator = ({ level }) => {
         const levels = [
             { label: 'Nominal', color: 'bg-green-600', icon: <Shield size={10} className="text-green-200" /> },
             { label: 'Low', color: 'bg-yellow-600', icon: <Shield size={10} className="text-yellow-200" /> },
             { label: 'Medium', color: 'bg-orange-600', icon: <Shield size={10} className="text-orange-200" /> },
             { label: 'High', color: 'bg-red-600 animate-pulse', icon: <Shield size={10} className="text-red-200" /> },
         ];
         const current = levels[level] || levels[0];
         return <div title={`Threat Level: ${current.label}`} className={`px-1.5 py-0.5 rounded text-white text-xxs flex items-center ${current.color}`}> {current.icon}<span className="ml-1">{current.label}</span></div>;
     }

    // --- Render ---
    const runningInstances = instances.filter(i => i.state === 'running');
    const totalRunningCapacity = runningInstances.reduce((sum, i) => sum + i.capacity, 0);
    const currentUtilization = totalRunningCapacity > 0 ? Math.min(100, Math.round(requestsPerSecond / totalRunningCapacity * 100)) : 0;


    return (
        <div className="bg-gradient-to-br from-black via-indigo-950 to-black text-gray-300 min-h-screen p-2 font-mono text-[11px] flex items-center justify-center">
            <div className="max-w-[1400px] w-full mx-auto bg-black/60 backdrop-blur-sm rounded-lg border border-blue-800/50 shadow-2xl shadow-blue-900/40 overflow-hidden">
                {/* Header */}
                 <header className="flex items-center justify-between mb-1.5 border-b border-blue-800/30 px-3 py-1 bg-black/30">
                     <div className="flex items-center">
                         {/* Using Server icon for Autoscale */}
                         <Server className="h-5 w-5 mr-2 text-cyan-300 animate-pulse" />
                         <h1 className="text-base font-bold text-gray-100">Foundry Autoscale <span className="text-xs text-cyan-400 font-normal">v5.2</span></h1> {/* Title updated */}
                     </div>
                     {/* Header Controls remain the same */}
                     <div className="flex items-center space-x-2 text-[10px]">
                         <span className="text-gray-400">Region: us-west-1b</span>
                         <div className="flex items-center" title={`AI Strategy: ${aiStrategy}`}>
                             <Zap size={10} className={`mr-1 ${aiStrategy === 'Performance' ? 'text-red-400' : aiStrategy === 'Cost-Optimized' ? 'text-green-400' : 'text-blue-400'}`} />
                             <span>{aiStrategy}</span>
                         </div>
                         <div className="flex items-center" title={`AI Confidence: ${(aiConfidence * 100).toFixed(0)}%`}>
                             <Gauge size={10} className={`mr-0.5 ${aiConfidence > 0.8 ? 'text-green-400' : aiConfidence < 0.5 ? 'text-red-400' : 'text-yellow-400'}`} />
                             <span>{(aiConfidence * 100).toFixed(0)}%</span>
                         </div>
                         <ThreatLevelIndicator level={threatLevel} />
                          <div className={`px-1.5 py-0.5 rounded text-white text-xxs min-w-[80px] text-center ${systemStatus.includes('CRITICAL') || systemStatus.includes('EMERGENCY') ? 'bg-red-700 animate-pulse' : systemStatus.includes('HIGH LOAD') ? 'bg-orange-600' : systemStatus.includes('SCALING') ? 'bg-blue-600' : systemStatus.includes('OPTIMIZING') ? 'bg-purple-600' : systemStatus.includes('UNDERUTILIZED') ? 'bg-yellow-600' : systemStatus.includes('NOMINAL') ? 'bg-green-600' : 'bg-gray-600'}`}>
                              {systemStatus}
                          </div>
                         <span className="text-gray-500 w-10 text-right">T+{time}s</span>
                     </div>
                 </header>

                {/* Main Content Grid */}
                 <div className="grid grid-cols-1 lg:grid-cols-3 gap-2 p-2">
                     {/* --- Left Column: Traffic & Instances --- */}
                     <div className="lg:col-span-2 space-y-2">
                         {/* Traffic Monitor */}
                         <div className="bg-gray-900/60 rounded-md p-2 border border-blue-800/30 shadow-inner">
                             <div className="flex items-center justify-between mb-1">
                                 <h2 className="text-gray-100 flex items-center text-sm font-semibold">
                                     <BarChart2 className="h-3.5 w-3.5 mr-1.5 text-cyan-400" /> Traffic Analysis & Prediction
                                 </h2>
                                 <div className="flex items-center space-x-2 text-xxs">
                                     <span title="API Requests">API: {(requestTypeDist.api * 100).toFixed(0)}%</span>
                                     <span title="Web Requests">Web: {(requestTypeDist.web * 100).toFixed(0)}%</span>
                                     <span title="Background Tasks">BG: {(requestTypeDist.background * 100).toFixed(0)}%</span>
                                 </div>
                             </div>
                             <div className="flex items-end space-x-4">
                                 <div className="flex-1">
                                      {/* Adjusted graph scaling factor for even lower expected peaks */}
                                     <div className="h-20 bg-gradient-to-b from-gray-950/50 to-black/50 rounded border border-blue-900/50 relative overflow-hidden">
                                         {[25, 50, 75].map(p => <div key={p} className="absolute left-0 right-0 border-t border-blue-900/30" style={{ bottom: `${p}%` }}></div>)}
                                         <svg className="absolute inset-0 w-full h-full" viewBox={`0 0 ${PREDICTION_HORIZON_S} 100`} preserveAspectRatio="none">
                                             <polyline
                                                 points={predictedLoadTrend.map((p, i) => `${i},${100 - Math.min(98, (p / 150) * 100)}`).join(' ')} /* Scaled to lower max (e.g., 150) */
                                                 fill="none" stroke="rgba(255, 255, 0, 0.4)" strokeWidth="0.5" strokeDasharray="2 2" />
                                         </svg>
                                         <div className="absolute bottom-0 left-0 w-full bg-gradient-to-t from-cyan-600/50 via-cyan-500/30 to-transparent transition-all duration-300 ease-out"
                                             style={{ height: `${Math.min(100, (requestsPerSecond / 150) * 100)}%` }} /* Scaled to lower max */ />
                                         <div className="absolute bottom-0 left-0 w-full border-t-2 border-cyan-300 transition-all duration-300 ease-out shadow-lg shadow-cyan-500/50"
                                             style={{ bottom: `${Math.min(99, (requestsPerSecond / 150) * 100)}%` }} /* Scaled to lower max */ />
                                     </div>
                                 </div>
                                 <div className="text-right">
                                     <div className="text-gray-400 text-xxs leading-tight">Current Load</div>
                                     <div className="text-2xl font-bold text-cyan-300 leading-none">{requestsPerSecond}</div>
                                     <div className="text-xs text-gray-400 leading-tight">req/s</div>
                                     <div className="text-gray-400 text-xxs mt-1 leading-tight">Prediction (next {PREDICTION_HORIZON_S}s)</div>
                                     <div className="text-sm font-semibold text-yellow-400 leading-none">{predictedLoadTrend[0]} - {predictedLoadTrend[PREDICTION_HORIZON_S - 1]}</div>
                                 </div>
                             </div>
                         </div>

                         {/* Instances Table */}
                         <div className="bg-gray-900/60 rounded-md p-2 border border-blue-800/30 shadow-inner">
                             <h2 className="text-gray-100 flex items-center mb-1 text-sm font-semibold">
                                 <Server className="h-3.5 w-3.5 mr-1.5 text-cyan-400" /> Compute Fleet Matrix
                             </h2>
                             <div className="overflow-x-auto max-h-[calc(100vh-430px)] min-h-[180px]"> {/* Adjusted height if needed */}
                                 <table className="w-full text-[10px] min-w-[650px]">
                                     <thead className="sticky top-0 bg-gray-950/80 backdrop-blur-sm z-10">
                                         <tr>
                                             <th className="text-left p-1 border-b border-blue-800/50 font-medium">ID</th>
                                             <th className="text-left p-1 border-b border-blue-800/50 font-medium">Type</th>
                                             <th className="text-left p-1 border-b border-blue-800/50 font-medium">Status</th>
                                             <th className="text-center p-1 border-b border-blue-800/50 font-medium" title="Instance Health">Health</th>
                                             <th className="text-right p-1 border-b border-blue-800/50 font-medium">Load (Req/s)</th>
                                             <th className="text-right p-1 border-b border-blue-800/50 font-medium">Util %</th>
                                             <th className="text-right p-1 border-b border-blue-800/50 font-medium" title="Capacity (Requests per second)">Cap</th>
                                             <th className="text-right p-1 border-b border-blue-800/50 font-medium" title="Configured Cost per Hour">$/hr</th>
                                         </tr>
                                     </thead>
                                     <tbody className="divide-y divide-blue-800/30">
                                         {instances.length === 0 ? (
                                             <tr><td colSpan="8" className="p-4 text-center text-gray-500 italic">Fleet Idle - Awaiting Directives...</td></tr>
                                         ) : (
                                             instances.map(instance => {
                                                 const typeInfo = instanceTypes.find(t => t.name === instance.type);
                                                 const utilPercent = instance.capacity > 0 ? Math.round((instance.load / instance.capacity) * 100) : 0;
                                                 return (
                                                     <tr key={instance.id} className={`hover:bg-blue-900/20 transition-colors duration-100 ${instance.state === 'terminating' ? 'opacity-40' : ''}`}>
                                                         <td className="p-1 whitespace-nowrap font-medium">[{instance.id.substring(0, 5)}]</td>
                                                         <td className="p-1 whitespace-nowrap text-gray-400">{instance.type}</td>
                                                         <td className="p-1 whitespace-nowrap">
                                                             <div className={`flex items-center ${getStatusColor(instance)}`}>
                                                                 {getStatusIcon(instance)}
                                                                 <span className="capitalize">{instance.state === 'starting' ? `Syncing (${Math.max(0, instance.readyTime - time)}s)` : instance.state === 'terminating' ? `Recycling` : instance.state}</span>
                                                             </div>
                                                         </td>
                                                         <td className="p-1 whitespace-nowrap text-center align-middle">
                                                             <div className="w-10 h-1.5 bg-gray-700 rounded-full mx-auto overflow-hidden" title={`Health: ${(instance.health * 100).toFixed(1)}%`}>
                                                                 <div className={`h-full rounded-full ${instance.health < 0.4 ? 'bg-red-500' : instance.health < 0.7 ? 'bg-orange-400' : 'bg-green-500'}`} style={{ width: `${instance.health * 100}%` }}></div>
                                                             </div>
                                                         </td>
                                                         <td className="p-1 whitespace-nowrap text-right">{instance.load}</td>
                                                         <td className="p-1 whitespace-nowrap text-right">
                                                             <span className={utilPercent > 90 ? 'text-red-400' : utilPercent > 70 ? 'text-orange-400' : utilPercent > 40 ? 'text-yellow-400' : 'text-green-400'}>{utilPercent}%</span>
                                                         </td>
                                                         <td className="p-1 text-right whitespace-nowrap text-gray-400">{instance.capacity}</td>
                                                         <td className="p-1 text-right whitespace-nowrap text-gray-500" title={`Configured Cost: $${typeInfo?.costPerHour?.toFixed(2)}/hr`}>${typeInfo?.costPerHour?.toFixed(2) || '?.??'}</td>
                                                     </tr>
                                                 );
                                             })
                                         )}
                                     </tbody>
                                 </table>
                             </div>
                         </div>
                     </div>

                     {/* --- Right Column: Status & Logs --- */}
                     <div className="lg:col-span-1 space-y-2">
                         {/* System Telemetry */}
                         <div className="bg-gray-900/60 rounded-md p-2 border border-blue-800/30 shadow-inner">
                              <h2 className="text-gray-100 flex items-center mb-1.5 text-sm font-semibold">
                                 <Cpu className="h-3.5 w-3.5 mr-1.5 text-cyan-400" /> System Telemetry
                              </h2>
                              <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[10px]">
                                 <div className="flex justify-between"> <span className="text-gray-400">Fleet Size:</span> <span className="font-medium">{instances.length} Units</span></div>
                                 <div className="flex justify-between"> <span className="text-gray-400">Total Capacity:</span> <span className="font-medium">{totalRunningCapacity} <span className="text-gray-500">req/s</span></span></div>
                                 <div className="flex justify-between items-center"> <span className="text-gray-400">Utilization:</span>
                                     <div className="flex items-center">
                                         <div className="w-10 h-1 bg-gray-700 rounded-full mr-1 overflow-hidden">
                                             <div className={`h-full rounded-full ${currentUtilization > 90 ? 'bg-red-500' : currentUtilization > 70 ? 'bg-orange-400' : currentUtilization > 40 ? 'bg-yellow-400' : 'bg-green-400'}`} style={{ width: `${currentUtilization}%` }}></div>
                                         </div>
                                         <span className="font-bold w-4 text-right">{currentUtilization}%</span>
                                     </div>
                                 </div>
                                 <div className="flex justify-between"> <span className="text-gray-400">Avg Latency:</span> <span className={avgLatency > 150 ? 'text-red-400' : avgLatency > 80 ? 'text-yellow-400' : 'text-green-400'}>{avgLatency} ms</span></div>
                                  <div className="flex justify-between"> <span className="text-gray-400">Est. Cost:</span> <span className="font-medium">${runningInstances.reduce((s, i) => {
                                       const typeInfo = instanceTypes.find(t => t.name === i.type);
                                       return s + (typeInfo?.costPerHour || 0);
                                   }, 0).toFixed(2)}/hr</span></div>
                                 <div className="flex justify-between"> <span className="text-gray-400">Efficiency:</span> <span className={costEfficiencyScore < 0.5 ? 'text-red-400' : costEfficiencyScore < 0.7 ? 'text-yellow-400' : 'text-green-400'}>{(costEfficiencyScore * 100).toFixed(0)}%</span></div>
                                 <div className="flex justify-between"> <span className="text-gray-400" title="Simulated Network Ingress/Egress">Network IO:</span> <span className="font-medium"><TrendingDown size={10} className="inline text-blue-400 -mt-px" />{networkIo.in.toFixed(1)} <TrendingUp size={10} className="inline text-green-400 -mt-px" />{networkIo.out.toFixed(1)} <span className="text-gray-500">Mbps</span></span></div>
                                 <div className="flex justify-between"> <span className="text-gray-400" title="Estimated Carbon Intensity">Carbon Est:</span> <span className="font-medium">{totalCarbonEstimate.toFixed(1)} <span className="text-gray-500">gCO2eq/h</span> <Wind size={10} className="inline text-cyan-400 -mt-px" /></span></div>
                              </div>
                          </div>

                         {/* Log Stream */}
                         <div className="bg-gray-900/60 rounded-md p-2 border border-blue-800/30 shadow-inner">
                             <h2 className="text-gray-100 flex items-center mb-1 text-sm font-semibold">
                                 <Activity className="h-3.5 w-3.5 mr-1.5 text-cyan-400" /> System Event Log
                             </h2>
                              <div className="bg-black/50 rounded border border-blue-900/50 h-[calc(100vh-430px)] min-h-[180px] overflow-y-auto p-1 text-[10px] font-mono scrollbar-thin scrollbar-thumb-blue-800/50 scrollbar-track-black/50">
                                 {logs.map(log => (
                                     <div key={log.id} className={`mb-0.5 flex items-start ${
                                         log.type === 'error' ? 'text-red-400' :
                                             log.type === 'warning' ? 'text-yellow-400' :
                                                 log.type === 'success' ? 'text-green-400' :
                                                     log.type === 'system' ? 'text-cyan-400' : 'text-gray-300'
                                         }`}>
                                         <span className="w-4 flex-shrink-0 mt-px mr-1 opacity-80">{log.icon || '-'}</span>
                                         <span className="text-gray-500 w-12 flex-shrink-0 mr-1">[{log.timestamp}]</span>
                                         <span className="flex-grow break-words leading-snug">{log.message}</span>
                                     </div>
                                 ))}
                                 <div ref={logsEndRef} />
                             </div>
                         </div>
                     </div>
                 </div>
            </div>
        </div>
    );
};

// Example usage with price override:
// <Autoscale overridePricing={{ 't4g.nano': 0.025, 'p4d.24xlarge': 3.20 }} />

// Example usage with default prices:
// <Autoscale />

export default Autoscale;